import React from 'react';
import {
    ChevronDown,
    ChevronRight,
    Play,
    CheckCircle,
    Clock,
    Loader2,
    Printer,
    RotateCcw,
    BookOpen,
    Pencil
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { formatTimeDisplay, formatIsoDateTimeShort } from '../../utils/timeUtils';
import { LessonDetailDto } from './types';
import { cn } from '../../lib/utils';
import { unitCardExpandableTitleClass } from '../unitCardTheme';
import { lessonLoadingStateKey, normalizeIdPart } from './loadingStateKeys';
import {
    getContentSteps,
    getLessonSteps,
    getOpenSessionId,
    getSessionDeliverySteps,
    hasOpenSession,
    isExternalResourceLesson,
    isLessonCompleted,
    isLessonContentComplete,
    isLessonInProgress,
    isLessonReady,
    isLessonStarted,
    LessonDeliveryModeKey,
    LessonStateKey,
    sumStepDurationMinutes
} from './lessonStepState';
import { useLazyCommand, useEvent } from '@soap/modules';
import { LessonSessionDialog } from '../dialogs/LessonSessionDialog';
import { CustomiseLessonDialog } from '../dialogs/CustomiseLessonDialog';
import { LessonCompletionDialog } from '../dialogs/LessonCompletionDialog';
import { formatHowDidLessonGoDisplay } from './lessonFeedbackUtils';
import { LessonPrintWizardDialog } from '../dialogs/LessonPrintWizardDialog';
import { InstructionStepCarousel } from './InstructionStepCarousel';
import { useInstructionStepCarousel } from './useInstructionStepCarousel';
import { LessonSupportingMaterialsSection } from './LessonSupportingMaterialsSection';
import { LessonStepProgressList } from './LessonStepProgressList';
import { LessonCustomisationHistorySection } from './LessonCustomisationHistorySection';
import {
    lessonsLessonElementId,
    scheduleScrollLessonCardIntoView
} from '../LessonsPage/utils/lessonUnitNavigation';
import { buildPatchFromE318 } from '../LessonsPage/utils/unitLessonPatches';
import { resolveLessonMaterials } from './instructionStepUtils';

interface LessonCardProps {
    lesson: LessonDetailDto;
    unitId: string;
    isExpanded: boolean;
    loadingStates: Set<string>;
    onToggle: (lessonId: string) => void;
    setLoadingStates: React.Dispatch<React.SetStateAction<Set<string>>>;
    onCollapseAllExcept?: (lessonId: string) => void;
    onSessionViewChange?: (focused: boolean) => void;
    /** True only for this lesson while its session view is showing — not for sibling lessons in the same unit. */
    sessionViewActive?: boolean;
    /** Collapse all lesson rows when this lesson is finished (before E261 scroll). */
    onLessonFinished?: () => void;
}

function formatLessonCovers(topicNames?: string[]): string | null {
    const covers = (topicNames ?? []).map(n => (n || '').trim()).filter(Boolean);
    return covers.length > 0 ? `Covers: ${covers.join(', ')}` : null;
}

export function LessonCard({
    lesson,
    unitId,
    isExpanded,
    loadingStates,
    onToggle,
    setLoadingStates,
    onCollapseAllExcept,
    onSessionViewChange,
    sessionViewActive = false,
    onLessonFinished
}: LessonCardProps) {
    const [displayOverride, setDisplayOverride] = React.useState<Partial<LessonDetailDto> | null>(null);

    const displayLesson = React.useMemo((): LessonDetailDto => {
        if (!displayOverride) return lesson;
        return { ...lesson, ...displayOverride };
    }, [lesson, displayOverride]);

    const displayLessonRef = React.useRef(displayLesson);
    displayLessonRef.current = displayLesson;

    React.useEffect(() => {
        if (!displayOverride) return;
        if (
            displayOverride.e251_LessonState === LessonStateKey.Completed &&
            isLessonCompleted(lesson)
        ) {
            setDisplayOverride(null);
            return;
        }
        if (
            displayOverride.e251_LessonState === LessonStateKey.InProgress &&
            isLessonInProgress(lesson)
        ) {
            setDisplayOverride(null);
        }
    }, [lesson, displayOverride]);

    const prevLessonIdRef = React.useRef<string | undefined>(undefined);
    React.useEffect(() => {
        const id = lesson.e251_LessonId || '';
        if (prevLessonIdRef.current !== undefined && prevLessonIdRef.current !== id) {
            setDisplayOverride(null);
            setSuppressFinishLessonButton(false);
        }
        prevLessonIdRef.current = id;
    }, [lesson.e251_LessonId]);

    const lessonId = lesson.e251_LessonId || '';
    const lessonDuration = displayLesson.e251_LessonDuration || 0;
    const lessonMaterials = React.useMemo(
        () => resolveLessonMaterials(displayLesson),
        [displayLesson]
    );
    const contentSteps = React.useMemo(
        () => getContentSteps(displayLesson),
        [displayLesson]
    );
    const openSessionId = React.useMemo(
        () => getOpenSessionId(displayLesson),
        [displayLesson]
    );
    const sessionSteps = React.useMemo(
        () => (openSessionId != null ? getSessionDeliverySteps(displayLesson, openSessionId) : []),
        [displayLesson, openSessionId]
    );

    const [sessionDialogOpen, setSessionDialogOpen] = React.useState(false);
    const [sessionDialogMode, setSessionDialogMode] = React.useState<'start' | 'continue'>('start');
    const [customiseDialogOpen, setCustomiseDialogOpen] = React.useState(false);
    const [completionDialogOpen, setCompletionDialogOpen] = React.useState(false);
    /** Hides Finish Lesson while the auto-prompted completion dialog is open; cleared on cancel. */
    const [suppressFinishLessonButton, setSuppressFinishLessonButton] = React.useState(false);
    const [printDialogOpen, setPrintDialogOpen] = React.useState(false);

    const {
        effectiveSteps: carouselSteps,
        canUseCarousel,
        isFlipped,
        setIsFlipped,
        currentIndex,
        handleIndexChange,
        currentStep: currentLessonStep
    } = useInstructionStepCarousel({
        unitId,
        lessonId,
        sessionId: openSessionId,
        steps: sessionSteps,
        enabled: openSessionId != null && sessionSteps.length > 0
    });

    const onSessionViewChangeRef = React.useRef(onSessionViewChange);
    onSessionViewChangeRef.current = onSessionViewChange;
    const dismissedStepsRef = React.useRef(false);

    const isSessionViewOpen = canUseCarousel && isFlipped;
    const sessionViewActiveFromParent = sessionViewActive;

    React.useLayoutEffect(() => {
        if (dismissedStepsRef.current) {
            if (!sessionViewActiveFromParent) dismissedStepsRef.current = false;
            return;
        }
        // Parent must pass sessionViewActive only for this lesson. If it is true for every
        // card in the unit, each open session would flip and overwrite the focused lesson.
        if (sessionViewActiveFromParent && canUseCarousel) {
            setIsFlipped(true);
        }
    }, [sessionViewActiveFromParent, canUseCarousel, setIsFlipped]);

    React.useLayoutEffect(() => {
        if (!isSessionViewOpen) return;
        onSessionViewChangeRef.current?.(true);
    }, [isSessionViewOpen]);

    React.useLayoutEffect(() => {
        return () => {
            onSessionViewChangeRef.current?.(false);
        };
    }, []);

    const enterSessionView = React.useCallback(() => {
        setIsFlipped(true);
        onSessionViewChangeRef.current?.(true);
    }, [setIsFlipped]);

    const exitSessionView = React.useCallback((options?: { scroll?: boolean }) => {
        dismissedStepsRef.current = true;
        onSessionViewChangeRef.current?.(false);
        setIsFlipped(false);
        if (options?.scroll === false || !lessonId) return;
        scheduleScrollLessonCardIntoView(unitId, lessonId, {
            delayMs: 280,
            block: 'center',
            force: true
        });
    }, [setIsFlipped, unitId, lessonId]);

    const completedMinutes = sumStepDurationMinutes(
        contentSteps.filter(s => s.e251_IsCompleted)
    );
    const totalContentMinutes = sumStepDurationMinutes(contentSteps);
    const remainingMinutes = Math.max(0, totalContentMinutes - completedMinutes);

    const isInProgress = isLessonInProgress(displayLesson);
    const isCompleted = isLessonCompleted(displayLesson);
    const isReady = isLessonReady(displayLesson);
    const hasSession = hasOpenSession(displayLesson);
    const allContentDone = isLessonContentComplete(displayLesson);
    const completedAtLabel = formatIsoDateTimeShort(displayLesson.e251_LessonCompletionDate ?? null);
    const coversLabel = formatLessonCovers(displayLesson.e251_TopicNames);
    const loadingKey = lessonLoadingStateKey(unitId, lessonId);
    const isLessonLocked = loadingStates.has(loadingKey);

    const isExternalLesson = isExternalResourceLesson(displayLesson);

    const [sendStartLessonCommand] = useLazyCommand('C268v1_StartLesson');
    const awaitingE258Ref = React.useRef(false);
    const [externalSessionPanelOpen, setExternalSessionPanelOpen] = React.useState(false);

    const [sendCompleteSessionCommand] = useLazyCommand('C327v1_CompleteLessonSession');
    const awaitingE318Ref = React.useRef(false);

    useEvent({
        eventName: 'E258v1_LessonStarted',
        onEventReceived: event => {
            const ev = event as Record<string, unknown>;
            const eLesson = ev.e258_LessonId ?? ev.E258_LessonId;
            const eUnit = ev.e258_UnitId ?? ev.E258_UnitId;
            if (
                !awaitingE258Ref.current ||
                normalizeIdPart(eLesson) !== normalizeIdPart(lessonId) ||
                normalizeIdPart(eUnit) !== normalizeIdPart(unitId)
            ) {
                return;
            }
            awaitingE258Ref.current = false;
            setLoadingStates(prev => {
                const next = new Set(prev);
                next.delete(loadingKey);
                return next;
            });
            const deliveryMode = String(ev.e258_DeliveryMode ?? ev.E258_DeliveryMode ?? '');
            if (deliveryMode === LessonDeliveryModeKey.ExternalResource) {
                setDisplayOverride({
                    e251_LessonState: LessonStateKey.InProgress,
                    e251_DeliveryMode: LessonDeliveryModeKey.ExternalResource,
                    e251_ActiveExternalSessionId: Number(ev.e258_SessionId ?? ev.E258_SessionId ?? 0) || 1
                });
                setExternalSessionPanelOpen(true);
                onSessionViewChangeRef.current?.(true);
            }
        }
    });

    useEvent({
        eventName: 'E318v1_LessonSessionCompleted',
        onEventReceived: event => {
            const ev = event as Record<string, unknown>;
            const eLesson = ev.e318_LessonId ?? ev.E318_LessonId;
            const eUnit = ev.e318_UnitId ?? ev.E318_UnitId;
            if (
                !awaitingE318Ref.current ||
                normalizeIdPart(eLesson) !== normalizeIdPart(lessonId) ||
                normalizeIdPart(eUnit) !== normalizeIdPart(unitId)
            ) {
                return;
            }
            awaitingE318Ref.current = false;
            setLoadingStates(prev => {
                const next = new Set(prev);
                next.delete(loadingKey);
                return next;
            });

            const patch = buildPatchFromE318(ev as Record<string, unknown>);
            const updatedLesson = { ...displayLessonRef.current, ...patch };
            const contentNowComplete = isLessonContentComplete(updatedLesson);

            exitSessionView();
            setExternalSessionPanelOpen(false);

            if (contentNowComplete) {
                setSuppressFinishLessonButton(true);
                setCompletionDialogOpen(true);
            }
        }
    });

    const handleStartExternalSession = async () => {
        if (isLessonLocked) return;
        awaitingE258Ref.current = true;
        setLoadingStates(prev => new Set(prev).add(loadingKey));
        try {
            await sendStartLessonCommand({
                C268_UnitId: unitId,
                C268_LessonId: lessonId,
                C268_SelectedStepIds: []
            });
        } catch (error) {
            console.error('Error starting external session:', error);
            awaitingE258Ref.current = false;
            setLoadingStates(prev => {
                const next = new Set(prev);
                next.delete(loadingKey);
                return next;
            });
        }
    };

    const handleCompleteSession = async () => {
        if (isLessonLocked) return;
        awaitingE318Ref.current = true;
        setLoadingStates(prev => new Set(prev).add(loadingKey));
        try {
            await sendCompleteSessionCommand({
                C327_UnitId: unitId,
                C327_LessonId: lessonId
            });
        } catch (error) {
            console.error('Error completing session:', error);
            awaitingE318Ref.current = false;
            setLoadingStates(prev => {
                const next = new Set(prev);
                next.delete(loadingKey);
                return next;
            });
        }
    };

    const focusLessonInAccordion = React.useCallback(() => {
        if (onCollapseAllExcept) {
            onCollapseAllExcept(lessonId);
        } else if (!isExpanded) {
            onToggle(lessonId);
        }
    }, [lessonId, isExpanded, onCollapseAllExcept, onToggle]);

    const [pendingPostLoad, setPendingPostLoad] = React.useState<null | 'expand'>(null);
    React.useEffect(() => {
        if (isLessonLocked || !pendingPostLoad) return;
        if (pendingPostLoad === 'expand' && !isExpanded) {
            onToggle(lessonId);
        }
        setPendingPostLoad(null);
    }, [isLessonLocked, pendingPostLoad, isExpanded, lessonId, onToggle]);

    const wasExpandedRef = React.useRef(isExpanded);
    React.useEffect(() => {
        const justExpanded = isExpanded && !wasExpandedRef.current;
        wasExpandedRef.current = isExpanded;
        if (!justExpanded || !lessonId || sessionViewActive) return;
        const handle = scheduleScrollLessonCardIntoView(unitId, lessonId);
        return () => window.clearTimeout(handle);
    }, [isExpanded, lessonId, unitId, sessionViewActive]);

    const handleApplyStartOptimistic = React.useCallback(() => {
        setDisplayOverride({ e251_LessonState: LessonStateKey.InProgress });
    }, []);

    const handleApplyFinishOptimistic = React.useCallback(() => {
        setDisplayOverride({
            e251_LessonState: LessonStateKey.Completed,
            e251_LessonCompletionDate: new Date().toISOString()
        });
    }, []);

    const handleRevertOptimistic = React.useCallback(() => {
        setDisplayOverride(null);
    }, []);

    const openSessionDialog = (mode: 'start' | 'continue') => {
        setSessionDialogMode(mode);
        setSessionDialogOpen(true);
    };

    const printAllSteps = React.useMemo(
        () => (isSessionViewOpen && carouselSteps.length > 0 ? carouselSteps : getLessonSteps(displayLesson)),
        [isSessionViewOpen, carouselSteps, displayLesson]
    );

    const handlePrint = () => {
        setPrintDialogOpen(true);
    };

    const handlePrintClick = () => {
        focusLessonInAccordion();
        handlePrint();
    };

    const lessonBody = (
        <>
            <div
                onClick={() => {
                    if (!isLessonLocked) onToggle(lessonId);
                }}
                className={cn(
                    'w-full px-4 py-3 text-left transition-[opacity,colors] duration-150',
                    isLessonLocked ? 'cursor-default opacity-75' : 'cursor-pointer',
                    !isLessonLocked && 'hover:bg-gray-50',
                    !isExpanded && 'rounded-lg'
                )}
            >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 flex-1 items-center">
                        {isExpanded ? (
                            <ChevronDown size={16} className="text-gray-500 mr-2 shrink-0" />
                        ) : (
                            <ChevronRight size={16} className="text-gray-500 mr-2 shrink-0" />
                        )}
                        {isLessonLocked && (
                            <Loader2
                                size={16}
                                className="mr-2 shrink-0 animate-spin text-action-blue"
                                aria-label="Updating lesson"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <h5 className={cn('text-base font-medium text-gray-900', unitCardExpandableTitleClass)}>
                                {displayLesson.e251_LessonTitle || 'Untitled Lesson'}
                            </h5>
                            {coversLabel && (
                                <p className="text-sm text-gray-500 mt-0.5">{coversLabel}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 flex-wrap">
                                <Clock size={12} className="mr-1" />
                                {formatTimeDisplay(lessonDuration)} total
                                {remainingMinutes > 0 && !isCompleted && (
                                    <span className="text-gray-600">
                                        • {formatTimeDisplay(remainingMinutes / 60)} remaining
                                    </span>
                                )}
                                {isCompleted && completedAtLabel && (
                                    <span className="text-gray-500">• Completed {completedAtLabel}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:shrink-0 sm:[&_button]:w-auto [&_button]:w-full"
                        onClick={e => e.stopPropagation()}
                    >
                        {(isReady || (isInProgress && !hasSession)) && !allContentDone && !isExternalLesson && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCustomiseDialogOpen(true)}
                                disabled={isLessonLocked || hasSession}
                                className="text-gray-600 border-gray-200 hover:bg-gray-50"
                            >
                                <Pencil size={14} className="mr-1" />
                                Customise with AI
                            </Button>
                        )}
                        {!isExternalLesson && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePrintClick}
                            disabled={isLessonLocked}
                            className="text-gray-600 border-gray-200 hover:bg-gray-50"
                            title="Print Lesson"
                        >
                            <Printer size={14} className="mr-1" />
                            Print
                        </Button>
                        )}
                        {canUseCarousel && hasSession && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={enterSessionView}
                                className="border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                            >
                                <BookOpen size={14} className="mr-1" />
                                Continue Session
                            </Button>
                        )}
                        {isReady && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    isExternalLesson
                                        ? void handleStartExternalSession()
                                        : openSessionDialog('start')
                                }
                                disabled={isLessonLocked}
                                className="border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                            >
                                <Play size={14} className="mr-1" />
                                Start Session
                            </Button>
                        )}
                        {isInProgress && !hasSession && !allContentDone && !isExternalLesson && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openSessionDialog('continue')}
                                disabled={isLessonLocked}
                                className="border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                            >
                                <Play size={14} className="mr-1" />
                                Continue Session
                            </Button>
                        )}
                        {isInProgress && hasSession && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void handleCompleteSession()}
                                disabled={isLessonLocked}
                                className="border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                            >
                                <CheckCircle size={14} className="mr-1 text-vibrant-green" />
                                Complete Session
                            </Button>
                        )}
                        {isInProgress && allContentDone && !suppressFinishLessonButton && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCompletionDialogOpen(true)}
                                disabled={isLessonLocked}
                                className="border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                            >
                                <CheckCircle size={14} className="mr-1 text-vibrant-green" />
                                Finish Lesson
                            </Button>
                        )}
                        {isCompleted && (
                            <span className="text-sm text-gray-500 flex items-center">
                                <CheckCircle size={14} className="mr-1" />
                                Completed
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <LessonCustomisationHistorySection entries={displayLesson.e251_PriorDeliveryGuidance} />

            <div className={cn(isLessonLocked && 'pointer-events-none opacity-70 select-none')}>
                {(() => {
                    const hasFeedback =
                        (displayLesson.e251_HowDidLessonGo &&
                            displayLesson.e251_HowDidLessonGo.trim()) ||
                        (displayLesson.e251_WhatCouldBeImproved &&
                            displayLesson.e251_WhatCouldBeImproved.trim());

                    return hasFeedback ? (
                        <div className="px-4 pb-3 border-t border-gray-100" onClick={e => e.stopPropagation()}>
                            <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 mt-3">
                                Lesson Feedback
                            </h6>
                            {displayLesson.e251_HowDidLessonGo &&
                                displayLesson.e251_HowDidLessonGo.trim() && (
                                    <div className="mb-2">
                                        <p className="text-xs font-medium text-gray-600 mb-1">
                                            How did the lesson go?
                                        </p>
                                        <p className="text-sm text-gray-700">
                                            {formatHowDidLessonGoDisplay(displayLesson.e251_HowDidLessonGo)}
                                        </p>
                                    </div>
                                )}
                            {displayLesson.e251_WhatCouldBeImproved &&
                                displayLesson.e251_WhatCouldBeImproved.trim() && (
                                    <div>
                                        <p className="text-xs font-medium text-gray-600 mb-1">
                                            What could be improved?
                                        </p>
                                        <p className="text-sm text-gray-700">
                                            {displayLesson.e251_WhatCouldBeImproved}
                                        </p>
                                    </div>
                                )}
                        </div>
                    ) : null;
                })()}

                {isExpanded && (
                    <div
                        className="px-4 pb-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-200"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="pt-4 space-y-4">
                            {contentSteps.length > 0 && (
                                <div>
                                    <div className="mb-1.5 text-xs font-semibold text-gray-800">
                                        Lesson steps
                                    </div>
                                    <LessonStepProgressList steps={contentSteps} />
                                </div>
                            )}
                            {displayLesson.e251_LessonSummary?.trim() && (
                                <div>
                                    <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                                        Summary
                                    </h6>
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                        {displayLesson.e251_LessonSummary}
                                    </p>
                                </div>
                            )}
                            <LessonSupportingMaterialsSection
                                unitId={unitId}
                                lessonId={lessonId}
                                lessonStarted={isLessonStarted(displayLesson)}
                                materialsToProvide={displayLesson.e251_LessonMaterials}
                                materials={lessonMaterials}
                                lessonTitle={displayLesson.e251_LessonTitle}
                                instructionSteps={displayLesson.e251_InstructionSteps}
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    const externalSessionViewPanel = (
        <div className="bg-white flex flex-col px-4 py-4 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h6 className="text-sm font-semibold text-gray-900">External lesson</h6>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleCompleteSession()}
                        disabled={isLessonLocked}
                    >
                        <CheckCircle size={14} className="mr-1 text-vibrant-green" />
                        Complete Session
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setExternalSessionPanelOpen(false);
                            onSessionViewChangeRef.current?.(false);
                        }}
                    >
                        <RotateCcw size={14} className="mr-1" />
                        Back To Lessons
                    </Button>
                </div>
            </div>
            <p className="text-sm text-gray-700">
                Use your{' '}
                <span className="font-medium">
                    {displayLesson.e251_ExternalProviderName || 'external resource'}
                </span>{' '}
                for this lesson. When you are done, press Complete Session to record progress.
            </p>
        </div>
    );

    const sessionViewPanel = (
        <div
            className={cn(
                'bg-white flex flex-col',
                sessionViewActive ? 'px-1 py-2 sm:px-2' : 'px-4 py-4 border-t border-gray-200'
            )}
        >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 shrink-0">
                <h6 className="text-sm font-semibold text-gray-900">Session steps</h6>
                <div className="flex flex-wrap items-center gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => setPrintDialogOpen(true)}>
                        <Printer size={14} className="mr-1" />
                        Print
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleCompleteSession()}
                        disabled={
                            isLessonLocked ||
                            carouselSteps.length === 0 ||
                            currentIndex < carouselSteps.length - 1
                        }
                        className="border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                    >
                        <CheckCircle size={14} className="mr-1 text-vibrant-green" />
                        Complete Session
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={e => {
                            e.stopPropagation();
                            exitSessionView();
                        }}
                        className="border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                    >
                        <RotateCcw size={14} className="mr-1" />
                        Back To Lessons
                    </Button>
                </div>
            </div>
            <div className="min-h-0 flex-1">
                <InstructionStepCarousel
                    steps={carouselSteps}
                    currentIndex={currentIndex}
                    onIndexChange={handleIndexChange}
                    supportingMaterials={lessonMaterials}
                    lessonTitle={displayLesson.e251_LessonTitle}
                    allLessonSteps={getLessonSteps(displayLesson)}
                />
            </div>
        </div>
    );

    return (
        <Card
            id={lessonId ? lessonsLessonElementId(unitId, lessonId) : undefined}
            aria-busy={isLessonLocked}
            className={cn(
                'bg-white transition-[box-shadow,border-color] duration-200 ease-out',
                sessionViewActiveFromParent && isSessionViewOpen
                    ? 'border-0 shadow-none'
                    : 'border border-gray-300',
                isLessonLocked && 'relative'
            )}
        >
            {sessionViewActiveFromParent && isSessionViewOpen
                ? sessionViewPanel
                : externalSessionPanelOpen && isExternalLesson
                  ? externalSessionViewPanel
                  : lessonBody}

            {sessionDialogOpen && (
                <LessonSessionDialog
                    unitId={unitId}
                    lessonId={lessonId}
                    lesson={displayLesson}
                    mode={sessionDialogMode}
                    setLoadingStates={setLoadingStates}
                    onApplyOptimistic={handleApplyStartOptimistic}
                    onRevertOptimistic={handleRevertOptimistic}
                    onClose={() => setSessionDialogOpen(false)}
                    onSuccess={() => {
                        setSessionDialogOpen(false);
                        setPendingPostLoad('expand');
                        enterSessionView();
                    }}
                />
            )}

            {customiseDialogOpen && (
                <CustomiseLessonDialog
                    unitId={unitId}
                    lessonId={lessonId}
                    lesson={displayLesson}
                    setLoadingStates={setLoadingStates}
                    onClose={() => setCustomiseDialogOpen(false)}
                    onSuccess={() => setCustomiseDialogOpen(false)}
                />
            )}

            {completionDialogOpen && (
                <LessonCompletionDialog
                    unitId={unitId}
                    lessonId={lessonId}
                    lesson={displayLesson}
                    setLoadingStates={setLoadingStates}
                    onApplyOptimistic={handleApplyFinishOptimistic}
                    onRevertOptimistic={handleRevertOptimistic}
                    onClose={() => {
                        setCompletionDialogOpen(false);
                        setSuppressFinishLessonButton(false);
                    }}
                    onSuccess={() => {
                        setCompletionDialogOpen(false);
                        setSuppressFinishLessonButton(false);
                        onLessonFinished?.();
                        exitSessionView({ scroll: false });
                    }}
                />
            )}

            <LessonPrintWizardDialog
                open={printDialogOpen}
                onOpenChange={setPrintDialogOpen}
                title={displayLesson.e251_LessonTitle || 'Lesson'}
                steps={printAllSteps}
                currentStep={currentLessonStep}
                supportingMaterials={lessonMaterials}
                lessonSteps={getLessonSteps(displayLesson)}
            />
        </Card>
    );
}
